/**
 * Data hook for searching supplier users when adding to a chat
 * Follows GraphQL separation pattern - handles GraphQL operations and maps to domain types
 */

import type { UserSelectOption } from '@choco-admin/types';
import { useCallback, useEffect, useMemo } from 'react';

import { useSearchSuppliersLazyQuery } from '../../../generated';

export interface UseSearchSupplierUsersConfig {
  supplierId: string;
}

export interface UseSearchSupplierUsersResponse {
  users: UserSelectOption[];
  isLoading: boolean;
  error: Error | null;
  searchUsers: (searchTerm: string) => Promise<void>;
  clearResults: () => void;
}

export const useSearchSupplierUsers = (
  config: UseSearchSupplierUsersConfig
): UseSearchSupplierUsersResponse => {
  const { supplierId } = config;

  const [executeSearch, { data, loading, error }] = useSearchSuppliersLazyQuery(
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Load initial data when supplierId is provided
  useEffect(() => {
    if (supplierId) {
      executeSearch({
        variables: {
          keywords: '',
          limit: 20,
        },
      });
    }
  }, [supplierId, executeSearch]);

  const searchUsers = useCallback(
    async (searchTerm: string) => {
      if (!supplierId) return;

      try {
        await executeSearch({
          variables: {
            keywords: searchTerm.trim(),
            limit: 20,
          },
        });
      } catch (err) {
        const usersError = err as Error;
        throw usersError;
      }
    },
    [executeSearch, supplierId]
  );

  const clearResults = useCallback(() => {
    // Clear results by executing a search with empty term
    executeSearch({
      variables: {
        keywords: '',
        limit: 0,
      },
    });
  }, [executeSearch]);

  const users = useMemo(() => {
    if (
      !data?.supplierSearch ||
      data.supplierSearch.__typename !== 'SearchSupplierConnection'
    ) {
      return [];
    }

    return data.supplierSearch.edges.map((edge) => ({
      value: edge.node.id,
      label: edge.node.name,
      metaData: {
        name: edge.node.name,
        supplierId: edge.node.id,
        phone: '',
        email: '',
        orderPreferences: [],
        supplier: true,
        referenceId: '',
      },
    }));
  }, [data]);

  return {
    users,
    isLoading: loading,
    error: error || null,
    searchUsers,
    clearResults,
  };
};
